/**
 * RitualSlotHUD -- four circular icons in the bottom-left HUD showing
 * equipped rituals with cooldown ring overlays. Keys 1-4 activate rituals.
 *
 * Design: GDD SS15.3 -- "UI is an uninvited guest". The HUD fades out
 * entirely when no rituals are equipped.
 *
 * Palette: cream (#eae0c8) on deep-blue (#1a1a2e), Almendra serif,
 * semi-transparent panels (alpha 0.45).
 */

import * as Phaser from 'phaser';
import type { OverworldRitualId } from '@game/shared';
import { MSG_RITUAL_START, OVERWORLD_RITUAL_PROTOCOL_VERSION } from '@game/shared';
import { FONT_PAINTERLY } from './FontManager';
import { hexToCSS } from './palette';

// ── Layout constants ───────────────────────────────────────────────────

const HUD_DEPTH = 1002;
const SLOT_RADIUS = 20;
const SLOT_DIAMETER = SLOT_RADIUS * 2;
const SLOT_GAP = 8;
const MARGIN_LEFT = 12;
const MARGIN_BOTTOM = 16;
const SLOT_COUNT = 4;

// Palette
const BG_COLOR = 0x1a1a2e;
const BG_ALPHA = 0.45;
const BORDER_COLOR = 0xeae0c8;
const BORDER_ALPHA = 0.6;
const EMPTY_BORDER_ALPHA = 0.2;
const KEY_LABEL_COLOR = '#eae0c8';
const COOLDOWN_ARC_COLOR = 0xeae0c8;
const COOLDOWN_ARC_ALPHA = 0.7;

// Per-ritual icon colors (simple colored circles until art exists)
const RITUAL_ICON_COLORS: Record<OverworldRitualId, number> = {
  'light-lantern': 0xffcc44,
  'sing-to-wind': 0x88ccff,
  'offer-at-shrine': 0xcc88ff,
  'plant-sapling': 0x66bb44,
};

const RITUAL_SHORT_NAMES: Record<OverworldRitualId, string> = {
  'light-lantern': 'Lantern',
  'sing-to-wind': 'Song',
  'offer-at-shrine': 'Offering',
  'plant-sapling': 'Sapling',
};

/** Default slot assignments -- all four core rituals pre-equipped. */
const DEFAULT_SLOTS: (OverworldRitualId | null)[] = [
  'light-lantern',
  'sing-to-wind',
  'offer-at-shrine',
  'plant-sapling',
];

// ── Types ──────────────────────────────────────────────────────────────

interface SlotRender {
  bg: Phaser.GameObjects.Graphics;
  icon: Phaser.GameObjects.Arc | null;
  cooldownArc: Phaser.GameObjects.Graphics;
  keyLabel: Phaser.GameObjects.Text;
  nameLabel: Phaser.GameObjects.Text;
}

interface CooldownState {
  startedAt: number;
  durationMs: number;
}

// ── Component ──────────────────────────────────────────────────────────

export class RitualSlotHUD {
  private scene: Phaser.Scene;
  private sendFn: ((type: string, data?: unknown) => void) | null;

  private slots: (OverworldRitualId | null)[];
  private renders: SlotRender[] = [];
  private cooldowns: (CooldownState | null)[] = [null, null, null, null];

  private keys: Phaser.Input.Keyboard.Key[] = [];
  private container: Phaser.GameObjects.Container;

  private hasAnyRitual = false;
  private containerAlpha = 0;

  constructor(
    scene: Phaser.Scene,
    sendFn: ((type: string, data?: unknown) => void) | null = null,
  ) {
    this.scene = scene;
    this.sendFn = sendFn;
    this.slots = [...DEFAULT_SLOTS];

    // Container positioned bottom-left
    this.container = scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(HUD_DEPTH);

    this.createSlots();
    this.wireInput();
    this.updateVisibility();
  }

  // ── Slot creation ──────────────────────────────────────────────────

  private createSlots(): void {
    const camH = this.scene.scale.height;
    const totalWidth = SLOT_COUNT * SLOT_DIAMETER + (SLOT_COUNT - 1) * SLOT_GAP;
    const startX = MARGIN_LEFT + SLOT_RADIUS;
    const baseY = camH - MARGIN_BOTTOM - SLOT_RADIUS - 16; // 16 for key label below

    for (let i = 0; i < SLOT_COUNT; i++) {
      const cx = startX + i * (SLOT_DIAMETER + SLOT_GAP);
      const cy = baseY;
      const ritualId = this.slots[i];

      // Background circle
      const bg = this.scene.add.graphics();
      this.drawSlotBg(bg, cx, cy, ritualId !== null);
      this.container.add(bg);

      // Ritual icon (colored circle) or null for empty
      let icon: Phaser.GameObjects.Arc | null = null;
      if (ritualId) {
        icon = this.scene.add.circle(cx, cy, SLOT_RADIUS * 0.55, RITUAL_ICON_COLORS[ritualId], 0.85);
        this.container.add(icon);
      }

      // Cooldown arc overlay
      const cooldownArc = this.scene.add.graphics();
      this.container.add(cooldownArc);

      // Key label ("1", "2", "3", "4")
      const keyLabel = this.scene.add.text(cx, cy + SLOT_RADIUS + 4, `${i + 1}`, {
        fontFamily: FONT_PAINTERLY,
        fontSize: '11px',
        color: KEY_LABEL_COLOR,
        align: 'center',
      });
      keyLabel.setOrigin(0.5, 0);
      this.container.add(keyLabel);

      // Name label (small, above slot)
      const nameText = ritualId ? RITUAL_SHORT_NAMES[ritualId] : '';
      const nameLabel = this.scene.add.text(cx, cy - SLOT_RADIUS - 12, nameText, {
        fontFamily: FONT_PAINTERLY,
        fontSize: '9px',
        color: KEY_LABEL_COLOR,
        align: 'center',
      });
      nameLabel.setOrigin(0.5, 1);
      nameLabel.setAlpha(0.7);
      this.container.add(nameLabel);

      this.renders.push({ bg, icon, cooldownArc, keyLabel, nameLabel });
    }

    // Route everything to UI camera if the scene supports it
    const sceneAny = this.scene as unknown as Record<string, ((...args: unknown[]) => void) | undefined>;
    sceneAny['addToUI']?.(this.container);
  }

  private drawSlotBg(gfx: Phaser.GameObjects.Graphics, cx: number, cy: number, filled: boolean): void {
    gfx.clear();
    // Background fill
    gfx.fillStyle(BG_COLOR, BG_ALPHA);
    gfx.fillCircle(cx, cy, SLOT_RADIUS);
    // Border
    const borderA = filled ? BORDER_ALPHA : EMPTY_BORDER_ALPHA;
    gfx.lineStyle(1, BORDER_COLOR, borderA);
    gfx.strokeCircle(cx, cy, SLOT_RADIUS);
  }

  // ── Input wiring ───────────────────────────────────────────────────

  private wireInput(): void {
    const kb = this.scene.input.keyboard;
    if (!kb) return;

    const keyCodes = [
      Phaser.Input.Keyboard.KeyCodes.ONE,
      Phaser.Input.Keyboard.KeyCodes.TWO,
      Phaser.Input.Keyboard.KeyCodes.THREE,
      Phaser.Input.Keyboard.KeyCodes.FOUR,
    ];

    for (let i = 0; i < SLOT_COUNT; i++) {
      const key = kb.addKey(keyCodes[i]!);
      const slotIndex = i;
      key.on('down', () => this.activateSlot(slotIndex));
      this.keys.push(key);
    }
  }

  private activateSlot(index: number): void {
    const ritualId = this.slots[index];
    if (!ritualId) return;

    // Check cooldown
    if (this.cooldowns[index]) {
      const cd = this.cooldowns[index]!;
      const elapsed = Date.now() - cd.startedAt;
      if (elapsed < cd.durationMs) return; // still on cooldown
      this.cooldowns[index] = null;
    }

    // Send ritual start to server
    if (this.sendFn) {
      this.sendFn(MSG_RITUAL_START, {
        v: OVERWORLD_RITUAL_PROTOCOL_VERSION,
        ritualId,
        locationX: 0, // server will use player's current position
        locationY: 0,
      });
    }
  }

  // ── Public API ─────────────────────────────────────────────────────

  /** Set the send function (e.g. after room join). */
  setSendFn(fn: (type: string, data?: unknown) => void): void {
    this.sendFn = fn;
  }

  /** Assign a ritual to a slot (0-3). Pass null to clear. */
  setSlot(index: number, ritualId: OverworldRitualId | null): void {
    if (index < 0 || index >= SLOT_COUNT) return;
    this.slots[index] = ritualId;
    this.refreshSlot(index);
    this.updateVisibility();
  }

  /** Notify the HUD that a ritual completed (triggers cooldown ring). */
  onRitualComplete(ritualId: OverworldRitualId, cooldownMs: number): void {
    const index = this.slots.indexOf(ritualId);
    if (index < 0) return;
    this.cooldowns[index] = {
      startedAt: Date.now(),
      durationMs: cooldownMs,
    };
  }

  /** Notify the HUD that a ritual was cancelled (clear cooldown). */
  onRitualCancelled(ritualId: OverworldRitualId): void {
    const index = this.slots.indexOf(ritualId);
    if (index < 0) return;
    this.cooldowns[index] = null;
  }

  /** Per-frame update -- draws cooldown arcs. */
  update(): void {
    const now = Date.now();
    const camH = this.scene.scale.height;
    const startX = MARGIN_LEFT + SLOT_RADIUS;
    const baseY = camH - MARGIN_BOTTOM - SLOT_RADIUS - 16;

    for (let i = 0; i < SLOT_COUNT; i++) {
      const render = this.renders[i];
      if (!render) continue;
      const cd = this.cooldowns[i];
      render.cooldownArc.clear();

      if (!cd) continue;

      const elapsed = now - cd.startedAt;
      if (elapsed >= cd.durationMs) {
        this.cooldowns[i] = null;
        continue;
      }

      // Draw cooldown arc: fills clockwise as the ritual recharges
      const progress = elapsed / cd.durationMs; // 0 -> 1
      const cx = startX + i * (SLOT_DIAMETER + SLOT_GAP);
      const cy = baseY;

      render.cooldownArc.lineStyle(3, COOLDOWN_ARC_COLOR, COOLDOWN_ARC_ALPHA);

      // Arc from top (- PI/2), clockwise by progress amount
      const startAngle = -Math.PI / 2;
      const endAngle = startAngle + progress * Math.PI * 2;

      render.cooldownArc.beginPath();
      render.cooldownArc.arc(cx, cy, SLOT_RADIUS - 2, startAngle, endAngle, false);
      render.cooldownArc.strokePath();
    }
  }

  destroy(): void {
    for (const key of this.keys) {
      key.removeAllListeners();
    }
    this.keys.length = 0;
    this.container.destroy(true);
    this.renders.length = 0;
  }

  // ── Internal ───────────────────────────────────────────────────────

  private refreshSlot(index: number): void {
    const render = this.renders[index];
    if (!render) return;
    const ritualId = this.slots[index];

    const camH = this.scene.scale.height;
    const cx = MARGIN_LEFT + SLOT_RADIUS + index * (SLOT_DIAMETER + SLOT_GAP);
    const cy = camH - MARGIN_BOTTOM - SLOT_RADIUS - 16;

    // Refresh background
    this.drawSlotBg(render.bg, cx, cy, ritualId !== null);

    // Refresh icon
    if (render.icon) {
      render.icon.destroy();
      render.icon = null;
    }
    if (ritualId) {
      render.icon = this.scene.add.circle(cx, cy, SLOT_RADIUS * 0.55, RITUAL_ICON_COLORS[ritualId], 0.85);
      this.container.add(render.icon);
    }

    // Refresh name
    render.nameLabel.setText(ritualId ? RITUAL_SHORT_NAMES[ritualId] : '');

    // Clear cooldown
    this.cooldowns[index] = null;
    render.cooldownArc.clear();
  }

  /** Fade the entire HUD based on whether any rituals are equipped.
   *  GDD SS15.3: "UI is an uninvited guest." */
  private updateVisibility(): void {
    const hasAny = this.slots.some(s => s !== null);
    if (hasAny === this.hasAnyRitual) return;
    this.hasAnyRitual = hasAny;

    const targetAlpha = hasAny ? 1 : 0;
    this.scene.tweens.add({
      targets: this.container,
      alpha: targetAlpha,
      duration: 600,
      ease: 'Sine.easeInOut',
    });
    this.containerAlpha = targetAlpha;
  }
}
